import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../utils/apiClient.js";
import NotificationModal from "../../components/NotificationModal.js";
import useNotification from "../../hooks/useNotification.js";
import useAutoRefresh from "../../hooks/useAutoRefresh.js";
import Spinner from "../../components/Spinner.js";
import Pagination from "../../components/Pagination.js";
import "./Admin.css";

const STATUSES = ["new", "read", "replied"];
const PER_PAGE = 10;

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const ContactMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { notification, notify, dismiss } = useNotification();
  const [savingId, setSavingId] = useState(null);

  // silent=true (background auto-refresh) skips the loading/error state changes so a live poll
  // never flashes "Loading…" over an already-rendered table or clobbers it with an error banner
  // over a transient network blip.
  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const { data } = await apiClient.get("/contact-messages", { params: { status, q, page, perPage: PER_PAGE } });
      setMessages(data.data);
      setTotal(data.total);
    } catch (err) {
      if (!silent) setError("Could not load contact messages.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [status, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Keeps this page live without a manual reload: refetches when the tab regains focus and on a
  // background interval, so an admin who leaves it open doesn't keep looking at a stale snapshot.
  useAutoRefresh(useCallback(() => load(true), [load]));

  const handleStatusChange = async (id, newStatus) => {
    setSavingId(id);
    try {
      await apiClient.post(`/contact-messages/${id}/status`, { status: newStatus });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: newStatus } : m)));
      notify("success", "Status updated", `Message marked as ${newStatus}.`);
    } catch (err) {
      notify("error", "Could not update status", "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Contact Messages</h1>
        <p>{total} total</p>
      </div>

      <div className="admin-filters">
        <input
          type="search"
          placeholder="Search by name or email…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="admin-error">{error}</p>}

      {loading ? (
        <Spinner label="Loading messages…" />
      ) : messages.length === 0 ? (
        <p>No contact messages found.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>S.N.</th>
                <th>Name</th>
                <th>Email</th>
                <th>Message</th>
                <th>Submitted</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m, index) => (
                <tr key={m.id}>
                  <td>{(page - 1) * PER_PAGE + index + 1}</td>
                  <td>{m.name}</td>
                  <td>{m.email}</td>
                  <td className="admin-details-cell">{m.message}</td>
                  <td>{formatDateTime(m.created_at)}</td>
                  <td>
                    <select
                      value={m.status}
                      disabled={savingId === m.id}
                      onChange={(e) => handleStatusChange(m.id, e.target.value)}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} perPage={PER_PAGE} total={total} onPageChange={setPage} />
        </div>
      )}

      {notification && (
        <NotificationModal
          variant={notification.variant}
          title={notification.title}
          message={notification.message}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default ContactMessagesPage;
